// @ts-nocheck
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Settings,
  Bell,
  CreditCard,
  Key,
  LogOut,
  ChevronDown,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import { goToRecruitLook } from '@/lib/recruitlookHandoff';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/contexts/AuthContext';
import { useAccessibleAreas } from '@/hooks/useAccessibleAreas';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface AreaUserMenuProps {
  /** Where the Settings item navigates to. */
  settingsPath?: string;
  /** Optional small role label shown next to the name in the trigger. */
  roleLabel?: string;
  /** Show name next to the avatar in the trigger (desktop). */
  showName?: boolean;
}

const AreaUserMenu: React.FC<AreaUserMenuProps> = ({
  settingsPath = '/account',
  roleLabel,
  showName = true,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, signOut } = useAuth();
  const { areas } = useAccessibleAreas();
  const [isHandingOff, setIsHandingOff] = useState(false);

  const displayName =
    (profile?.first_name && profile?.last_name
      ? `${profile.first_name} ${profile.last_name}`
      : profile?.name) ||
    (user?.email ? user.email.split('@')[0] : 'User');

  const initials = (() => {
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name[0]}${profile.last_name[0]}`.toUpperCase();
    }
    return displayName.charAt(0).toUpperCase();
  })();

  const isCurrentArea = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch {
      /* noop */
    }
    navigate('/login');
  };

  const handleResetPassword = async () => {
    const email = profile?.email || user?.email;
    if (!email) {
      toast.error('Email not found');
      return;
    }
    try {
      const { error } = await invokeEdgeFunction('send-password-reset', {
        body: { email },
      });
      if (error) throw error;
      toast.success('Password reset email sent! Check your inbox.');
    } catch (error) {
      console.error('Error sending reset email:', error);
      toast.error('Failed to send password reset email');
    }
  };

  const handleCoachDashboard = async () => {
    setIsHandingOff(true);
    try {
      await goToRecruitLook('/club-coach-dashboard');
    } finally {
      // Navigation replaces the page; if it fails, re-enable the item.
      setIsHandingOff(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-2 rounded-full pl-1 pr-2 py-1 hover:bg-muted/60 transition outline-none">
        <Avatar className="h-9 w-9">
          <AvatarImage src={profile?.avatar_url || ''} alt={displayName} />
          <AvatarFallback className="bg-primary text-white text-sm font-semibold">
            {initials}
          </AvatarFallback>
        </Avatar>
        {showName && (
          <span className="hidden lg:flex flex-col items-start leading-tight">
            <span className="text-sm font-semibold text-foreground truncate max-w-[140px]">
              {displayName}
            </span>
            {roleLabel && (
              <span className="text-xs text-muted-foreground">{roleLabel}</span>
            )}
          </span>
        )}
        <ChevronDown className="h-4 w-4 text-muted-foreground" />
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col">
            <span className="text-sm font-medium text-foreground truncate">
              {displayName}
            </span>
            <span className="text-xs text-muted-foreground truncate">
              {user?.email}
            </span>
          </div>
        </DropdownMenuLabel>

        <>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground">
            My Dashboards
          </DropdownMenuLabel>
          {areas.map((area) => (
            <DropdownMenuItem
              key={area.path}
              onClick={() => navigate(area.path)}
              disabled={isCurrentArea(area.path)}
              className="cursor-pointer"
            >
              <LayoutDashboard className="mr-2 h-4 w-4" />
              <span>{area.label}</span>
              {isCurrentArea(area.path) && (
                <span className="ml-auto text-[10px] uppercase tracking-wide text-muted-foreground">
                  Current
                </span>
              )}
            </DropdownMenuItem>
          ))}
          <DropdownMenuItem
            onClick={handleCoachDashboard}
            disabled={isHandingOff}
            className="cursor-pointer"
          >
            {isHandingOff ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <ExternalLink className="mr-2 h-4 w-4" />
            )}
            <span>Coach dashboard</span>
          </DropdownMenuItem>
        </>

        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => navigate(settingsPath)}
          className="cursor-pointer"
        >
          <Settings className="mr-2 h-4 w-4" />
          <span>Account settings</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => navigate('/account')}
          className="cursor-pointer"
        >
          <Bell className="mr-2 h-4 w-4" />
          <span>Notification Settings</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => navigate('/billing')}
          className="cursor-pointer"
        >
          <CreditCard className="mr-2 h-4 w-4" />
          <span>Billing</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleResetPassword} className="cursor-pointer">
          <Key className="mr-2 h-4 w-4" />
          <span>Reset Password</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleSignOut}
          className="cursor-pointer text-red-600 focus:text-red-600"
        >
          <LogOut className="mr-2 h-4 w-4" />
          <span>Sign Out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AreaUserMenu;
